import { useCallback, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import type { InvitedModes } from "api/models/draftRequest.model";
import {
  GetFreightModesConfigResponseType,
  GetServiceDetailsConfigResponseType,
  AvailableFreightMode,
} from "api/response/location.response";
import { syncSpecialRequirementsFromConfig } from "@RequestFormV2/sections/Service/utils/syncSpecialRequirementsFromConfig";
import { useEditShipmentLock } from "@RequestFormV2/context/editShipmentLock";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import {
  buildDefaultInvitedModes,
  buildInvitedModesAfterSelectAllType,
  buildInvitedModesAfterToggle,
  getSelectedInvitedModeValues,
} from "@RequestFormV2/utils/invitedModesHelpers";
import { resolveDefaultServiceType } from "@RequestFormV2/utils/serviceTypeOptions";
import { resolveDefaultIncoterms } from "@RequestFormV2/utils/resolveDefaultIncoterms";
import { isInsuranceUnset } from "@RequestFormV2/validation/sections/validateService";
import type { SelectOption } from "components/v2/Select/types";

function flattenAvailableModes(
  freightModesConfig: GetFreightModesConfigResponseType["freight_modes"] | undefined,
): AvailableFreightMode[] {
  return freightModesConfig?.available_modes ?? [];
}

function getSpecialRequirementKeySignature(
  specialRequirements: Record<string, unknown> | undefined,
): string {
  return Object.keys(specialRequirements ?? {})
    .sort()
    .join(",");
}

export function useServiceHandlers(
  freightModesConfig: GetFreightModesConfigResponseType["freight_modes"] | undefined,
  serviceDetailsConfig: GetServiceDetailsConfigResponseType | undefined,
  serviceTypeOptions: SelectOption[],
) {
  const { setValue, getValues } = useFormContext();
  const isEditShipmentLock = useEditShipmentLock();
  const saveContext = useRequestFormSaveOrNull();
  const invitedModes = useWatch({ name: "invited_modes" }) as InvitedModes | undefined;
  const specialRequirements = useWatch({ name: "service.special_requirements" }) as
    | Record<string, { label?: string; value?: boolean | string; additional_information?: string }>
    | undefined;
  const availableModes = flattenAvailableModes(freightModesConfig);

  const configuredRequirementKeysSignature = useMemo(() => {
    const values = serviceDetailsConfig?.service_options?.request_service_requirement_values ?? [];
    return values
      .map((item) => item.value)
      .sort()
      .join(",");
  }, [serviceDetailsConfig]);

  const draftRequirementKeysSignature = useMemo(
    () => getSpecialRequirementKeySignature(specialRequirements),
    [specialRequirements],
  );

  useEffect(() => {
    if (isEditShipmentLock) return;
    if (!freightModesConfig?.pre_select_freight_mode) return;
    if (getSelectedInvitedModeValues(invitedModes).length > 0) return;

    const defaults = buildDefaultInvitedModes(availableModes);
    setValue("invited_modes", defaults);
    saveContext?.saveInvitedModes();
    // Only run when freight mode config first loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freightModesConfig]);

  useEffect(() => {
    if (!serviceTypeOptions.length) return;

    const currentValue = getValues("service.service_type") as string | null | undefined;
    const nextValue = resolveDefaultServiceType(serviceTypeOptions, currentValue);
    if (nextValue && nextValue !== currentValue) {
      setValue("service.service_type", nextValue);
      saveContext?.saveField("service.service_type", nextValue).catch(() => undefined);
    }
    // Only run when service type options first load or location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTypeOptions]);

  useEffect(() => {
    if (isEditShipmentLock) return;
    if (!serviceDetailsConfig) return;
    if (serviceDetailsConfig.service_options?.incoterm === "off") return;

    const currentValue = getValues("service.incoterms") as string | null | undefined;
    const nextValue = resolveDefaultIncoterms(
      serviceDetailsConfig.service_options?.incoterm_default,
      currentValue,
    );
    if (nextValue && nextValue !== currentValue) {
      setValue("service.incoterms", nextValue);
      saveContext?.saveField("service.incoterms", nextValue).catch(() => undefined);
    }
    // Only run when service details config first loads or location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDetailsConfig]);

  useEffect(() => {
    if (!serviceDetailsConfig) return;

    const askInsurance = serviceDetailsConfig.service_options?.ask_insurance;
    if (askInsurance && askInsurance !== "off") {
      const currentInsurance = getValues("service.insurance");
      if (isInsuranceUnset(currentInsurance)) {
        setValue("service.insurance", false);
      }
    }
  }, [serviceDetailsConfig, getValues, setValue]);

  useEffect(() => {
    if (!serviceDetailsConfig) return;

    const serviceOptions = serviceDetailsConfig.service_options;
    if (!serviceOptions?.request_service_requirement) {
      const existing = (getValues("service.special_requirements") ?? {}) as Record<string, unknown>;
      if (Object.keys(existing).length > 0) {
        setValue("service.special_requirements", {});
        saveContext?.saveField("service.special_requirements", {}).catch(() => undefined);
      }
      return;
    }

    const existing = (getValues("service.special_requirements") ?? {}) as Record<
      string,
      { label?: string; value?: boolean | string; additional_information?: string }
    >;
    const next = syncSpecialRequirementsFromConfig(
      serviceOptions.request_service_requirement_values ?? [],
      existing,
    );
    const hasStaleKeys = Object.keys(existing).some((key) => !(key in next));
    const hasChanges = JSON.stringify(existing) !== JSON.stringify(next);

    if (!hasChanges) {
      return;
    }

    setValue("service.special_requirements", next);
    if (hasStaleKeys) {
      saveContext?.saveField("service.special_requirements", next).catch(() => undefined);
    }
  }, [
    serviceDetailsConfig,
    configuredRequirementKeysSignature,
    draftRequirementKeysSignature,
    getValues,
    setValue,
    saveContext,
  ]);

  const persistInvitedModes = useCallback(
    (next: InvitedModes) => {
      setValue("invited_modes", next);
      saveContext?.saveInvitedModes();
    },
    [setValue, saveContext],
  );

  const handleToggleMode = useCallback(
    (value: string) => {
      const current = getValues("invited_modes") as InvitedModes | undefined;
      const next = buildInvitedModesAfterToggle(current, value, availableModes);
      persistInvitedModes(next);
    },
    [getValues, availableModes, persistInvitedModes],
  );

  const handleRoadFreightOptionChange = useCallback(
    (optionValue: string | null) => {
      const current = getValues("invited_modes") as InvitedModes | undefined;
      const next = { ...(current ?? {}), road_freight_option: optionValue ?? "" };
      persistInvitedModes(next);
    },
    [getValues, persistInvitedModes],
  );

  const handleSelectAllType = useCallback(
    (typeValues: string[], allSelected: boolean) => {
      const current = getValues("invited_modes") as InvitedModes | undefined;
      const next = buildInvitedModesAfterSelectAllType(current, typeValues, allSelected, availableModes);
      persistInvitedModes(next);
    },
    [getValues, availableModes, persistInvitedModes],
  );

  return useMemo(
    () => ({ handleToggleMode, handleSelectAllType, handleRoadFreightOptionChange }),
    [handleToggleMode, handleSelectAllType, handleRoadFreightOptionChange],
  );
}
